import { formatDisplayDate } from '../lib/dateFormatting';

function formatDate(value) {
  return formatDisplayDate(value, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }, 'Unknown');
}

function formatDateTime(value) {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function titleCaseStatus(value, fallback = 'Pending') {
  const normalized = String(value || '').trim();
  if (!normalized) return fallback;
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalizeText(value) {
  return String(value ?? '').trim();
}

function isUnknownValue(value) {
  const normalized = normalizeText(value).toLowerCase();
  return (
    !normalized
    || normalized === 'pending'
    || normalized === 'unknown'
    || normalized === 'not specified in baseline'
    || normalized === 'not available'
  );
}

function displayValue(value, fallback = 'Unknown') {
  return isUnknownValue(value) ? fallback : normalizeText(value);
}

function extractNumbers(value) {
  const matches = normalizeText(value).match(/\d+/g);
  return matches ? matches.map(Number).filter((entry) => Number.isFinite(entry)) : [];
}

function buildSignalRow({
  key,
  label,
  value,
  chipLabel,
  tone,
  tooltipTitle = label,
  tooltipWhatItMeans,
  tooltipInvestorRead,
  sourceValue,
}) {
  return {
    key,
    label,
    value,
    chipLabel,
    tone,
    tooltip: {
      title: tooltipTitle,
      whatItMeans: tooltipWhatItMeans,
      investorRead: tooltipInvestorRead,
      loadedDbValue: displayValue(sourceValue, 'Not listed'),
    },
  };
}

function joinCompactParts(parts) {
  return parts.filter(Boolean).join(' • ');
}

function parseDayCount(value) {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return null;
  const match = normalized.match(/(\d+)\s*day/);
  return match ? Number(match[1]) : null;
}

function booleanStatusFromValue(value, { allowMeaningfulTextAsYes = false } = {}) {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized || isUnknownValue(normalized)) return 'unknown';
  if (/^(yes|true|1)\b/.test(normalized)) return 'yes';
  if (/^(no|false|0|none|n\/a|not applicable)\b/.test(normalized)) return 'no';
  if (allowMeaningfulTextAsYes) return 'yes';
  return 'unknown';
}

function buildFactsIndex(payload) {
  const facts = {};
  const groups = Array.isArray(payload?.groups) ? payload.groups : [];
  const highlights = Array.isArray(payload?.highlights) ? payload.highlights : [];

  [...highlights, ...groups.flatMap((group) => group?.rows || [])].forEach((row) => {
    if (!row?.key || facts[row.key]) return;
    facts[row.key] = row;
  });

  return facts;
}

function deriveRegulatoryFriction(factsByKey) {
  const noticeDays = parseDayCount(factsByKey.nonpayment_notice_period?.valueDisplay);
  const appealDays = parseDayCount(factsByKey.appeal_time?.valueDisplay);
  const cureStatus = booleanStatusFromValue(
    factsByKey.cure_available_for?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const backRentStatus = booleanStatusFromValue(
    factsByKey.back_rent_can_halt_case?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const justCauseStatus = booleanStatusFromValue(
    factsByKey.just_cause_requirement?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const automaticSealingStatus = booleanStatusFromValue(
    factsByKey.automatic_record_inaccessibility?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const requestSealingStatus = booleanStatusFromValue(
    factsByKey.tenant_can_request_record_inaccessibility?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const saleProtectionStatus = booleanStatusFromValue(
    factsByKey.sale_related_tenant_protections?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const foreclosureProtectionStatus = booleanStatusFromValue(
    factsByKey.foreclosure_related_tenant_protections?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );

  let score = 0;
  let signals = 0;

  if (noticeDays != null) {
    signals += 1;
    if (noticeDays >= 7) score += 2;
    else if (noticeDays >= 4) score += 1;
  }

  if (cureStatus !== 'unknown') {
    signals += 1;
    if (cureStatus === 'yes') score += 1;
  }

  if (backRentStatus !== 'unknown') {
    signals += 1;
    if (backRentStatus === 'yes') score += 1;
  }

  if (justCauseStatus !== 'unknown') {
    signals += 1;
    if (justCauseStatus === 'yes') score += 2;
  }

  if (appealDays != null) {
    signals += 1;
    if (appealDays >= 10) score += 1;
  }

  if (automaticSealingStatus !== 'unknown' || requestSealingStatus !== 'unknown') {
    signals += 1;
    if (automaticSealingStatus === 'yes' || requestSealingStatus === 'yes') score += 1;
  }

  if (saleProtectionStatus !== 'unknown' || foreclosureProtectionStatus !== 'unknown') {
    signals += 1;
    if (saleProtectionStatus === 'yes' || foreclosureProtectionStatus === 'yes') score += 1;
  }

  if (signals < 2) {
    return {
      label: 'Unknown',
      tone: 'unknown',
      detail: 'Not enough featured fields are available to summarize friction cleanly.',
    };
  }

  if (score <= 1) {
    return {
      label: 'Lower',
      tone: 'lower',
      detail: 'Shorter notice or fewer featured protection signals appear in this baseline.',
    };
  }

  if (score <= 4) {
    return {
      label: 'Moderate',
      tone: 'moderate',
      detail: 'This baseline shows a mix of owner process steps and tenant protections.',
    };
  }

  return {
    label: 'Higher',
    tone: 'higher',
    detail: 'Longer notice or more featured protection signals appear in this baseline.',
  };
}

function buildInvestorRead({
  jurisdictionName,
  source,
  friction,
  noticeRow,
  cureRow,
  appealRow,
}) {
  const noticeValue = displayValue(noticeRow?.valueDisplay);
  const cureValue = displayValue(cureRow?.valueDisplay);
  const appealValue = displayValue(appealRow?.valueDisplay);
  const noticeDays = parseDayCount(noticeValue);
  const appealDays = parseDayCount(appealValue);
  const baselineDate = source?.baselineAsOfLabel || formatDate(source?.baselineAsOf);
  const locationName = jurisdictionName || 'This state';

  if (friction.label === 'Unknown') {
    return (
      `${locationName} has incomplete featured LSC fields in this snapshot, so the investor read is limited; `
      + `laws may have changed since ${baselineDate}.`
    );
  }

  const curePhrase = isUnknownValue(cureValue)
    ? 'unclear cure rules'
    : `cure rights for ${cureValue.toLowerCase()}`;
  const noticePhrase = isUnknownValue(noticeValue)
    ? 'an unclear nonpayment notice baseline'
    : noticeDays != null
      ? `a ${noticeDays}-day nonpayment notice baseline`
      : `${noticeValue.toLowerCase()} nonpayment notice rules`;
  const appealPhrase = isUnknownValue(appealValue)
    ? 'unclear appeal timing'
    : appealDays != null
      ? `a ${appealDays}-day appeal window`
      : `${appealValue.toLowerCase()} appeal timing`;

  return (
    `${locationName} shows ${noticePhrase}, ${curePhrase}, and ${appealPhrase} in this LSC snapshot, suggesting `
    + `${friction.label.toLowerCase()} state-level process friction; laws may have changed since ${baselineDate}.`
  );
}

function buildTimelineSteps(factsByKey) {
  const noticeValue = displayValue(factsByKey.nonpayment_notice_period?.valueDisplay);
  const otherNoticeValue = displayValue(factsByKey.other_notice_periods?.valueDisplay);
  const cureValue = displayValue(factsByKey.cure_available_for?.valueDisplay);
  const backRentValue = displayValue(factsByKey.back_rent_can_halt_case?.valueDisplay);
  const backRentWindowValue = displayValue(factsByKey.back_rent_cure_window?.valueDisplay);
  const courtValue = displayValue(factsByKey.filing_court?.valueDisplay);
  const appealValue = displayValue(factsByKey.appeal_time?.valueDisplay);
  const appealBondValue = displayValue(factsByKey.appeal_bond_requirement?.valueDisplay);
  const writValue = displayValue(factsByKey.time_to_execute_writ?.valueDisplay);
  const removalEntityValue = displayValue(factsByKey.writ_execution_entity?.valueDisplay, 'Not listed');
  const filingDetail = joinCompactParts([
    isUnknownValue(cureValue) ? null : `Cure: ${cureValue}`,
    isUnknownValue(backRentWindowValue)
      ? (isUnknownValue(backRentValue) ? null : `Back-rent halt: ${backRentValue}`)
      : `Back-rent cure: ${backRentWindowValue}`,
  ]);
  const appealDetail = joinCompactParts([
    isUnknownValue(appealValue) ? null : `Appeal: ${appealValue}`,
    isUnknownValue(appealBondValue) ? null : `Bond: ${appealBondValue}`,
  ]);

  return [
    {
      key: 'notice-required',
      label: 'Notice Required',
      value: noticeValue,
      detail: isUnknownValue(otherNoticeValue)
        ? 'Nonpayment notice before filing'
        : `Other notice: ${otherNoticeValue}`,
    },
    {
      key: 'filing-allowed',
      label: 'Filing Allowed',
      value: isUnknownValue(noticeValue) ? 'Unknown' : `After ${noticeValue}`,
      detail: filingDetail || 'Featured filing rules not specified',
    },
    {
      key: 'court-judgment',
      label: 'Court / Judgment',
      value: courtValue,
      detail: appealDetail || 'Appeal and bond rules not specified',
    },
    {
      key: 'possession',
      label: 'Possession',
      value: writValue,
      detail: `Removal by: ${removalEntityValue}`,
    },
  ];
}

function buildInvestorSignal({ tone, symbol, label, description }) {
  return {
    tone,
    symbol,
    label,
    description,
  };
}

function classifyInvestorSignal(row) {
  const rowKey = normalizeText(row?.key);
  const value = row?.valueDisplay;
  const normalizedValue = normalizeText(value).toLowerCase();
  const booleanStatus = booleanStatusFromValue(value, { allowMeaningfulTextAsYes: true });
  const numbers = extractNumbers(value);
  const minNumber = numbers.length ? Math.min(...numbers) : null;
  const maxNumber = numbers.length ? Math.max(...numbers) : null;
  const friendlySignal = (description) => buildInvestorSignal({
    tone: 'friendly',
    symbol: '✓',
    label: 'Friendly',
    description,
  });
  const notFriendlySignal = (description) => buildInvestorSignal({
    tone: 'not-friendly',
    symbol: '×',
    label: 'Not friendly',
    description,
  });
  const neutralSignal = (description) => buildInvestorSignal({
    tone: 'neutral',
    symbol: '•',
    label: 'Neutral',
    description,
  });

  switch (rowKey) {
    case 'nonpayment_notice_period':
    case 'other_notice_periods':
      if (minNumber == null) return neutralSignal('Notice timing is not clear enough here to grade as owner-favorable or owner-unfavorable.');
      if (minNumber <= 3) return friendlySignal('Shorter notice timing generally gives an owner a faster path to filing.');
      if (minNumber >= 7) return notFriendlySignal('Longer notice timing generally adds more pre-filing delay for an owner.');
      return neutralSignal('Mid-range notice timing is mixed and depends on deal strategy and local practice.');
    case 'cure_available_for':
      if (booleanStatus === 'yes') return notFriendlySignal('A listed cure right gives the tenant another chance to stop or delay the case.');
      if (booleanStatus === 'no') return friendlySignal('No cure right listed is generally more owner-favorable in process terms.');
      return neutralSignal('Cure treatment is not clear enough here to mark as owner-favorable or owner-unfavorable.');
    case 'back_rent_can_halt_case':
      if (booleanStatus === 'yes') return notFriendlySignal('Allowing back rent to halt the case can add tenant protection and reduce owner leverage.');
      if (booleanStatus === 'no') return friendlySignal('If paying back rent does not halt the case, owner process flexibility is generally stronger.');
      return neutralSignal('Back-rent treatment is not clear enough here to score directionally.');
    case 'back_rent_cure_window':
      if (isUnknownValue(value)) return neutralSignal('The back-rent cure window is not specific enough here to grade.');
      return notFriendlySignal('A defined back-rent cure window generally adds tenant process protection.');
    case 'appeal_time':
      if (minNumber == null) return neutralSignal('Appeal timing is not specific enough here to grade.');
      if (minNumber <= 3) return friendlySignal('A shorter appeal window can favor faster possession and lower delay risk.');
      if (minNumber >= 10) return notFriendlySignal('A longer appeal window can increase delay risk after judgment.');
      return neutralSignal('A mid-range appeal window is mixed and not strongly directional by itself.');
    case 'appeal_bond_requirement':
      if (booleanStatus === 'yes') return friendlySignal('An appeal bond requirement can make appeals harder to pursue and may favor owners.');
      if (booleanStatus === 'no') return notFriendlySignal('No appeal bond requirement can reduce friction for an appeal.');
      return neutralSignal('Appeal bond treatment is not clear enough here to grade.');
    case 'time_to_execute_writ':
      if (minNumber == null) return neutralSignal('Execution timing is not specified clearly enough here to grade.');
      if (minNumber <= 3) return friendlySignal('A shorter writ-execution window can support faster possession.');
      if (minNumber >= 7) return notFriendlySignal('A longer writ-execution window can add post-judgment delay.');
      return neutralSignal('Execution timing is mixed and not strongly directional by itself.');
    case 'mediation_available':
      if (booleanStatus === 'yes') return notFriendlySignal('Mediation availability can add another process step before possession is resolved.');
      if (booleanStatus === 'no') return friendlySignal('No mediation step listed may reduce one possible process layer.');
      return neutralSignal('Mediation treatment is not clear enough here to grade.');
    case 'tenant_defenses_available':
      if (booleanStatus === 'yes') return notFriendlySignal('Listed tenant defenses can increase contestability and process friction for owners.');
      if (booleanStatus === 'no') return friendlySignal('No listed tenant defenses is relatively more owner-favorable procedurally.');
      return neutralSignal('Defense treatment is not clear enough here to grade.');
    case 'just_cause_requirement':
      if (booleanStatus === 'yes') return notFriendlySignal('A just-cause rule can limit owner flexibility around nonrenewal or termination.');
      if (booleanStatus === 'no') return friendlySignal('No just-cause rule listed is generally more owner-flexible.');
      return neutralSignal('Just-cause treatment is not clear enough here to grade.');
    case 'sale_related_tenant_protections':
    case 'foreclosure_related_tenant_protections':
      if (booleanStatus === 'yes') return notFriendlySignal('Tenant protections tied to transfer events can reduce owner flexibility around turnover.');
      if (booleanStatus === 'no') return friendlySignal('No listed transfer-related tenant protection is generally more owner-flexible.');
      return neutralSignal('Transfer-event protection treatment is not clear enough here to grade.');
    case 'automatic_record_inaccessibility':
    case 'tenant_can_request_record_inaccessibility':
    case 'record_access_controls':
    case 'unlawful_eviction_protections':
    case 'primary_service_methods':
    case 'required_notice_contents':
    case 'filing_court':
    case 'sel_filing_fee':
    case 'sel_notice_to_vacate':
    case 'sel_summons_content':
    case 'sel_secondary_service':
    case 'sel_max_late_fee':
      return neutralSignal('This field is useful legal context, but it is not a clean owner-favorable or owner-unfavorable signal by itself.');
    case 'sel_response':
      if (normalizedValue.includes('not always required') || normalizedValue.includes('not required')) {
        return friendlySignal('A response step that is not always required is generally more owner-favorable procedurally.');
      }
      if (normalizedValue.includes('required')) {
        return notFriendlySignal('A required response step can add more process structure before disposition.');
      }
      return neutralSignal('Response treatment is not clear enough here to grade.');
    case 'sel_summons_time':
      if (maxNumber == null) return neutralSignal('Summons timing is not clear enough here to grade.');
      if (maxNumber <= 7) return friendlySignal('A shorter summons-to-hearing window is generally more owner-favorable on timing.');
      if (maxNumber > 14) return notFriendlySignal('A longer summons-to-hearing window can add more time before resolution.');
      return neutralSignal('Mid-range summons timing is mixed and not strongly directional by itself.');
    case 'sel_appeal_stay':
    case 'sel_stay_execution':
    case 'sel_stay_issuance':
    case 'sel_stay_request':
      if (booleanStatus === 'yes') return notFriendlySignal('A stay or stay-request path can delay possession after judgment or appeal.');
      if (booleanStatus === 'no') return friendlySignal('No listed stay path is generally more owner-favorable on post-judgment timing.');
      if (!isUnknownValue(value)) return notFriendlySignal('A listed stay mechanism can increase post-judgment delay risk for an owner.');
      return neutralSignal('Stay treatment is not clear enough here to grade.');
    case 'sel_stay_time':
      return neutralSignal('Stay duration context is useful, but it is not a clean favorable or unfavorable signal by itself.');
    case 'sel_late_fee':
      if (normalizedValue.includes('permit')) return friendlySignal('Permitting late fees can support owner collections and lease enforcement.');
      if (normalizedValue.includes('prohibit')) return notFriendlySignal('Prohibiting late fees can reduce owner flexibility on enforcement.');
      return neutralSignal('Late-fee treatment is not clear enough here to grade.');
    default:
      return neutralSignal('This field does not map cleanly to an investor-friendly or investor-unfriendly process signal on its own.');
  }
}

function buildProtectionSignalGroups(factsByKey) {
  const automaticSealingRow = factsByKey.automatic_record_inaccessibility;
  const requestSealingRow = factsByKey.tenant_can_request_record_inaccessibility;
  const recordAccessControlsRow = factsByKey.record_access_controls;
  const justCauseValue = displayValue(factsByKey.just_cause_requirement?.valueDisplay, 'Not listed');
  const saleProtectionValue = displayValue(factsByKey.sale_related_tenant_protections?.valueDisplay, 'Not listed');
  const foreclosureProtectionValue = displayValue(factsByKey.foreclosure_related_tenant_protections?.valueDisplay, 'Not listed');
  const cureValue = displayValue(factsByKey.cure_available_for?.valueDisplay, 'Not listed');
  const unlawfulEvictionValue = displayValue(factsByKey.unlawful_eviction_protections?.valueDisplay, 'Not listed');
  const automaticSealingValue = displayValue(automaticSealingRow?.valueDisplay, 'Not listed');
  const requestSealingValue = displayValue(requestSealingRow?.valueDisplay, 'Not listed');
  const recordAccessControlsValue = displayValue(recordAccessControlsRow?.valueDisplay, 'Not listed');
  const justCauseStatus = booleanStatusFromValue(
    factsByKey.just_cause_requirement?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const saleProtectionStatus = booleanStatusFromValue(
    factsByKey.sale_related_tenant_protections?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const foreclosureProtectionStatus = booleanStatusFromValue(
    factsByKey.foreclosure_related_tenant_protections?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const cureStatus = booleanStatusFromValue(
    factsByKey.cure_available_for?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const unlawfulEvictionStatus = booleanStatusFromValue(
    factsByKey.unlawful_eviction_protections?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const automaticSealingStatus = booleanStatusFromValue(
    automaticSealingRow?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );
  const requestSealingStatus = booleanStatusFromValue(
    requestSealingRow?.valueDisplay,
    { allowMeaningfulTextAsYes: true },
  );

  let evictionSealing = {
    value: 'Verify locally',
    chipLabel: 'Verify',
    tone: 'verify',
  };

  if (automaticSealingRow || requestSealingRow) {
    if (automaticSealingStatus === 'yes') {
      evictionSealing = {
        value: 'Present',
        chipLabel: 'Compliance',
        tone: 'compliance',
      };
    } else if (requestSealingStatus === 'yes') {
      evictionSealing = {
        value: 'Present',
        chipLabel: 'Compliance',
        tone: 'compliance',
      };
    } else if (automaticSealingStatus === 'no' && requestSealingStatus === 'no') {
      evictionSealing = {
        value: 'None specified',
        chipLabel: 'Compliance',
        tone: 'compliance',
      };
    } else {
      evictionSealing = {
        value: 'Verify locally',
        chipLabel: 'Verify',
        tone: 'verify',
      };
    }
  }

  return [
    {
      key: 'owner-flexibility',
      title: 'Owner Flexibility',
      rows: [
        buildSignalRow({
          key: 'just-cause',
          label: 'Just Cause',
          value: justCauseStatus === 'yes'
            ? 'Required'
            : justCauseStatus === 'no'
              ? 'Not required'
              : 'Verify locally',
          chipLabel: justCauseStatus === 'yes'
            ? 'Friction'
            : justCauseStatus === 'no'
              ? 'Flexible'
              : 'Verify',
          tone: justCauseStatus === 'yes'
            ? 'friction'
            : justCauseStatus === 'no'
              ? 'owner'
              : 'verify',
          tooltipTitle: 'Just Cause',
          tooltipWhatItMeans: 'A just-cause rule requires a legally approved reason before an owner can terminate, decline renewal, or evict in certain situations.',
          tooltipInvestorRead: 'If not required, the market usually gives owners more flexibility at lease end, but owners still must follow notice, lease, fair-housing, anti-retaliation, and court rules.',
          sourceValue: justCauseValue,
        }),
        buildSignalRow({
          key: 'sale-protections',
          label: 'Sale Protections',
          value: saleProtectionStatus === 'yes'
            ? 'Present'
            : saleProtectionStatus === 'no'
              ? 'None specified'
              : 'Verify locally',
          chipLabel: saleProtectionStatus === 'yes'
            ? 'Friction'
            : saleProtectionStatus === 'no'
              ? 'Flexible'
              : 'Verify',
          tone: saleProtectionStatus === 'yes'
            ? 'friction'
            : saleProtectionStatus === 'no'
              ? 'owner'
              : 'verify',
          tooltipTitle: 'Sale Protections',
          tooltipWhatItMeans: 'Sale protections are tenant rules that continue or activate when a rental property is sold or transferred to a new owner.',
          tooltipInvestorRead: 'If none are specified, the dataset does not show extra sale-triggered tenant protections. Existing leases, deposits, notices, and normal eviction rules can still apply.',
          sourceValue: saleProtectionValue,
        }),
        buildSignalRow({
          key: 'foreclosure-protections',
          label: 'Foreclosure Protections',
          value: foreclosureProtectionStatus === 'yes'
            ? 'Present'
            : foreclosureProtectionStatus === 'no'
              ? 'None specified'
              : 'Verify locally',
          chipLabel: foreclosureProtectionStatus === 'yes'
            ? 'Friction'
            : foreclosureProtectionStatus === 'no'
              ? 'Flexible'
              : 'Verify',
          tone: foreclosureProtectionStatus === 'yes'
            ? 'friction'
            : foreclosureProtectionStatus === 'no'
              ? 'owner'
              : 'verify',
          tooltipTitle: 'Foreclosure Protections',
          tooltipWhatItMeans: 'Foreclosure protections are tenant rules that may apply after foreclosure or a related ownership transfer.',
          tooltipInvestorRead: 'If present, expect extra notice or process requirements before removing occupants after foreclosure. This matters most for distressed or auction acquisitions.',
          sourceValue: foreclosureProtectionValue,
        }),
      ],
    },
    {
      key: 'process-friction',
      title: 'Process Friction',
      rows: [
        buildSignalRow({
          key: 'right-to-cure',
          label: 'Right to Cure',
          value: cureStatus === 'yes'
            ? 'Present'
            : cureStatus === 'no'
              ? 'Not specified'
              : 'Verify locally',
          chipLabel: cureStatus === 'yes'
            ? 'Friction'
            : cureStatus === 'no'
              ? 'Flexible'
              : 'Verify',
          tone: cureStatus === 'yes'
            ? 'friction'
            : cureStatus === 'no'
              ? 'owner'
              : 'verify',
          tooltipTitle: 'Right to Cure',
          tooltipWhatItMeans: 'A right to cure gives the tenant a chance to fix the problem, such as paying rent or correcting a lease violation, before the case can fully proceed.',
          tooltipInvestorRead: 'This can add process steps and delay, but it also creates a clean compliance path if documented properly.',
          sourceValue: cureValue,
        }),
        buildSignalRow({
          key: 'cure-details',
          label: 'Cure Details',
          value: cureStatus === 'yes'
            ? cureValue
            : cureStatus === 'no'
              ? 'Not listed'
              : 'Verify locally',
          chipLabel: cureStatus === 'yes'
            ? 'Listed'
            : cureStatus === 'no'
              ? 'Flexible'
              : 'Verify',
          tone: cureStatus === 'yes'
            ? 'friction'
            : cureStatus === 'no'
              ? 'owner'
              : 'verify',
          tooltipTitle: 'Cure Details',
          tooltipWhatItMeans: 'This lists which problems the tenant may be allowed to fix, such as nonpayment of rent or certain lease breaches.',
          tooltipInvestorRead: 'The broader the cure list, the more carefully the owner must document notices, deadlines, payment status, and lease violations.',
          sourceValue: cureValue,
        }),
      ],
    },
    {
      key: 'compliance-unknowns',
      title: 'Compliance / Unknowns',
      rows: [
        buildSignalRow({
          key: 'unlawful-eviction',
          label: 'Unlawful Eviction Protections',
          value: unlawfulEvictionStatus === 'yes'
            ? 'Present'
            : unlawfulEvictionStatus === 'no'
              ? 'None specified'
              : 'Verify locally',
          chipLabel: unlawfulEvictionStatus === 'unknown'
            ? 'Verify'
            : 'Compliance',
          tone: unlawfulEvictionStatus === 'unknown' ? 'verify' : 'compliance',
          tooltipTitle: 'Unlawful Eviction Protections',
          tooltipWhatItMeans: 'These are rules that limit improper removal, lockouts, discrimination, retaliation, or other non-court enforcement actions.',
          tooltipInvestorRead: 'This is mainly a compliance signal. Do not rely on self-help removal. Use notices, documentation, and the formal legal process.',
          sourceValue: unlawfulEvictionValue,
        }),
        buildSignalRow({
          key: 'eviction-sealing',
          label: 'Eviction Sealing',
          value: evictionSealing.value,
          chipLabel: evictionSealing.chipLabel,
          tone: evictionSealing.tone,
          tooltipTitle: 'Eviction Sealing',
          tooltipWhatItMeans: 'Eviction sealing or record inaccessibility rules affect whether eviction filings or outcomes remain publicly visible.',
          tooltipInvestorRead: 'This can affect tenant screening reliability. If the value is unknown or not listed, verify with local court records and screening vendors.',
          sourceValue: `Automatic: ${automaticSealingValue} | Request: ${requestSealingValue} | Access: ${recordAccessControlsValue}`,
        }),
      ],
    },
  ];
}

function buildDetailRows(groups, source) {
  return groups.flatMap((group) => (
    (group?.rows || []).map((row) => ({
      key: `${group?.key || 'group'}-${row?.key || row?.label || 'row'}`,
      field: row?.label || 'Field',
      value: displayValue(row?.valueDisplay),
      citation: row?.citationText || source?.name || 'Source not listed',
      effectiveDate: (
        source?.jurisdictionEffectiveDateLabel
        || source?.baselineAsOfLabel
        || formatDate(source?.jurisdictionEffectiveDate || source?.baselineAsOf)
      ),
      notes: row?.cautionText || '—',
      investorSignal: classifyInvestorSignal(row),
    }))
  ));
}

function tenantProtectionFreshnessLabel(value, fallback = 'Pending') {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return fallback;
  if (normalized === 'stale') return 'Stale upstream';
  if (normalized === 'fresh') return 'Overlay loaded';
  return titleCaseStatus(value, fallback);
}

function RegulationKpiCard({ card }) {
  return (
    <article className="regulation-snapshot__kpi-card">
      <span className="regulation-snapshot__kpi-label">{card.label}</span>
      <strong className={`regulation-snapshot__kpi-value regulation-snapshot__kpi-value--${card.tone || 'default'}`}>
        {card.value}
      </strong>
      <span className="regulation-snapshot__kpi-subtext">{card.subtext}</span>
    </article>
  );
}

function RegulationOverlayStat({ stat }) {
  return (
    <article className="regulation-snapshot__overlay-stat">
      <span className="regulation-snapshot__overlay-stat-label">{stat.label}</span>
      <strong className="regulation-snapshot__overlay-stat-value">{stat.value}</strong>
      <span className="regulation-snapshot__overlay-stat-subtext">{stat.subtext}</span>
    </article>
  );
}

function RegulationTimelineStep({ step, index }) {
  const stepMetaByKey = {
    'notice-required': { tone: 'notice' },
    'filing-allowed': { tone: 'filing' },
    'court-judgment': { tone: 'court' },
    possession: { tone: 'possession' },
  };
  const stepMeta = stepMetaByKey[step.key] || { tone: 'default' };

  return (
    <div
      className={`regulation-snapshot__timeline-step regulation-snapshot__timeline-step--${stepMeta.tone} regulation-snapshot__timeline-step--${index + 1}`}
    >
      <div className="regulation-snapshot__timeline-icon-wrap" aria-hidden="true">
        <span>{index + 1}</span>
      </div>
      <div className="regulation-snapshot__timeline-content">
        <div className="regulation-snapshot__timeline-head">
          <span className="regulation-snapshot__timeline-label">{step.label}</span>
        </div>
        <strong className="regulation-snapshot__timeline-value">{step.value}</strong>
        <span className="regulation-snapshot__timeline-detail">{step.detail}</span>
      </div>
    </div>
  );
}

function RegulationSignalGroup({
  group,
  openTooltipKey,
  onOpenTooltip,
  onCloseTooltip,
  onToggleTooltip,
}) {
  return (
    <section className="regulation-snapshot__signal-group">
      <h4 className="regulation-snapshot__signal-group-title">{group.title}</h4>
      <div className="regulation-snapshot__signal-list">
        {group.rows.map((row) => (
          <div key={row.key} className="regulation-snapshot__signal-row">
            <div className="regulation-snapshot__signal-main">
              <span className="regulation-snapshot__signal-label">{row.label}</span>
              <div
                className="regulation-snapshot__signal-meta"
                onMouseEnter={() => onOpenTooltip(row.key)}
                onMouseLeave={() => onCloseTooltip(row.key)}
                onFocus={() => onOpenTooltip(row.key)}
                onBlur={(event) => {
                  if (event.currentTarget.contains(event.relatedTarget)) return;
                  onCloseTooltip(row.key);
                }}
              >
                <button
                  type="button"
                  className="regulation-snapshot__signal-trigger"
                  aria-label={`More detail about ${row.label}`}
                  aria-expanded={openTooltipKey === row.key}
                  aria-describedby={openTooltipKey === row.key ? `regulation-signal-tooltip-${row.key}` : undefined}
                  onClick={() => onToggleTooltip(row.key)}
                >
                  i
                </button>
                {openTooltipKey === row.key ? (
                  <div
                    id={`regulation-signal-tooltip-${row.key}`}
                    className="regulation-snapshot__signal-popover"
                    role="tooltip"
                  >
                    <strong className="regulation-snapshot__signal-popover-title">{row.tooltip.title}</strong>
                    <div className="regulation-snapshot__signal-popover-section">
                      <span className="regulation-snapshot__signal-popover-label">What it means</span>
                      <p className="regulation-snapshot__signal-popover-text">{row.tooltip.whatItMeans}</p>
                    </div>
                    <div className="regulation-snapshot__signal-popover-section">
                      <span className="regulation-snapshot__signal-popover-label">Investor read</span>
                      <p className="regulation-snapshot__signal-popover-text">{row.tooltip.investorRead}</p>
                    </div>
                    <div className="regulation-snapshot__signal-popover-footer">
                      <span className="regulation-snapshot__signal-popover-label">Loaded DB value</span>
                      <span className="regulation-snapshot__signal-popover-db">{row.tooltip.loadedDbValue}</span>
                      <span className="regulation-snapshot__signal-popover-note">State baseline only — verify locally.</span>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <span className="regulation-snapshot__signal-value">{row.value}</span>
            <div className="regulation-snapshot__signal-actions">
              <span className={`regulation-snapshot__signal-chip regulation-snapshot__signal-chip--${row.tone}`}>
                {row.chipLabel}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RegulationTenantProtectionOverlay({ overlay, compact = false }) {
  const source = overlay?.source || {};
  const records = Array.isArray(overlay?.records) ? overlay.records : [];
  const categorySummary = Array.isArray(overlay?.categorySummary) ? overlay.categorySummary : [];
  const stats = Array.isArray(overlay?.stats) ? overlay.stats : [];
  const freshnessTone = String(source?.freshnessStatus || 'pending').trim().toLowerCase() || 'pending';
  const freshnessLabel = tenantProtectionFreshnessLabel(source?.freshnessStatus, overlay?.statusLabel || 'Pending');

  if (compact) {
    return (
      <details className="regulation-snapshot__details regulation-snapshot__details--overlay">
        <summary>View Policy Records</summary>
        {records.length ? (
          <div className="regulation-snapshot__overlay-table-wrap">
            <table className="regulation-snapshot__overlay-table">
              <thead>
                <tr>
                  <th scope="col">Scope</th>
                  <th scope="col">Category</th>
                  <th scope="col">Status</th>
                  <th scope="col">Record</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.key}>
                    <td>
                      <div className="regulation-snapshot__overlay-scope">
                        <strong>{row.scopeName}</strong>
                        <span>
                          {row.scopeLabel}
                          {row.implementingAuthority ? ` • ${row.implementingAuthority}` : ''}
                          {row.yearPassed ? ` • ${row.yearPassed}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="regulation-snapshot__overlay-category">
                        <strong>{row.categoryPrimary}</strong>
                        {row.extraCategoryCount > 0 ? <span>{`+${row.extraCategoryCount} more`}</span> : null}
                      </div>
                    </td>
                    <td>
                      <span className={`regulation-snapshot__overlay-status-chip regulation-snapshot__overlay-status-chip--${row.statusTone}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="regulation-snapshot__overlay-record">
                        {row.protectionNumber ? <strong>{row.protectionNumber}</strong> : null}
                        <p title={row.descriptionText}>{row.descriptionShort}</p>
                        {row.linkUrl ? (
                          <a className="regulation-snapshot__overlay-link" href={row.linkUrl} target="_blank" rel="noreferrer">
                            Source
                          </a>
                        ) : (
                          <span className="regulation-snapshot__overlay-link regulation-snapshot__overlay-link--muted">
                            Source not listed
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="regulation-snapshot__empty regulation-snapshot__empty--overlay">
            <p>{overlay?.emptyMessage || 'No tenant-protection policy records are loaded for this state yet.'}</p>
          </div>
        )}
      </details>
    );
  }

  return (
    <section className="regulation-snapshot__panel regulation-snapshot__panel--overlay">
      <div className="regulation-snapshot__panel-head regulation-snapshot__panel-head--overlay">
        <div>
          <h3>Current Tenant Protection Overlay</h3>
          <p>Compact NLIHC state + local policy rows layered beneath the older LSC baseline.</p>
        </div>
        <div className={`regulation-snapshot__overlay-status regulation-snapshot__overlay-status--${freshnessTone}`}>
          {overlay?.status === 'ready' ? freshnessLabel : (overlay?.statusLabel || 'Pending')}
        </div>
      </div>

      <p className="regulation-snapshot__overlay-note">
        {overlay?.note || 'Local rows may not apply across the full metro. Verify the exact jurisdiction before relying on a local ordinance.'}
      </p>

      {overlay?.status === 'ready' ? (
        <>
          <div className="regulation-snapshot__overlay-summary">
            {stats.map((stat) => (
              <RegulationOverlayStat key={stat.key || stat.label} stat={stat} />
            ))}
          </div>

          {categorySummary.length ? (
            <div className="regulation-snapshot__overlay-chip-block">
              <span className="regulation-snapshot__overlay-chip-label">Active categories</span>
              <div className="regulation-snapshot__overlay-chip-list">
                {categorySummary.map((category) => (
                  <span
                    key={category.label}
                    className="regulation-snapshot__overlay-chip"
                    title={`${category.label}: ${category.count}`}
                  >
                    {category.label}
                    <strong>{category.count}</strong>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <details className="regulation-snapshot__details regulation-snapshot__details--overlay">
            <summary>{`View Policy Records (${records.length})`}</summary>
            <div className="regulation-snapshot__overlay-table-wrap">
              <table className="regulation-snapshot__overlay-table">
                <thead>
                  <tr>
                    <th scope="col">Scope</th>
                    <th scope="col">Category</th>
                    <th scope="col">Status</th>
                    <th scope="col">Record</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <div className="regulation-snapshot__overlay-scope">
                          <strong>{row.scopeName}</strong>
                          <span>
                            {row.scopeLabel}
                            {row.implementingAuthority ? ` • ${row.implementingAuthority}` : ''}
                            {row.yearPassed ? ` • ${row.yearPassed}` : ''}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="regulation-snapshot__overlay-category">
                          <strong>{row.categoryPrimary}</strong>
                          {row.extraCategoryCount > 0 ? (
                            <span>{`+${row.extraCategoryCount} more`}</span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <span className={`regulation-snapshot__overlay-status-chip regulation-snapshot__overlay-status-chip--${row.statusTone}`}>
                          {row.status}
                        </span>
                      </td>
                      <td>
                        <div className="regulation-snapshot__overlay-record">
                          {row.protectionNumber ? (
                            <strong>{row.protectionNumber}</strong>
                          ) : null}
                          <p title={row.descriptionText}>{row.descriptionShort}</p>
                          {row.linkUrl ? (
                            <a
                              className="regulation-snapshot__overlay-link"
                              href={row.linkUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Source
                            </a>
                          ) : (
                            <span className="regulation-snapshot__overlay-link regulation-snapshot__overlay-link--muted">
                              Source not listed
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      ) : (
        <div className="regulation-snapshot__empty regulation-snapshot__empty--overlay">
          <p>{overlay?.emptyMessage || 'No NLIHC overlay rows are loaded for this state yet.'}</p>
        </div>
      )}

      <div className="regulation-snapshot__overlay-foot">
        <span>
          Source period {source?.latestSourcePeriodLabel || 'Unknown'}
        </span>
        <span>
          Imported {source?.lastImportedAtLabel || 'Unknown'}
        </span>
        {Array.isArray(source?.links) && source.links.length ? (
          <span className="regulation-snapshot__overlay-foot-links">
            {source.links.map((link, index) => (
              <span key={link.href}>
                {index ? ' | ' : ''}
                <a
                  className="regulation-snapshot__overlay-link"
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {link.label}
                </a>
              </span>
            ))}
          </span>
        ) : null}
      </div>
    </section>
  );
}

export default function LandlordRegulationSnapshotSection({ loading, error, payload }) {
  const source = payload?.source || {};
  const groups = Array.isArray(payload?.groups) ? payload.groups : [];
  const factsByKey = buildFactsIndex(payload);
  const friction = deriveRegulatoryFriction(factsByKey);
  const timelineSteps = buildTimelineSteps(factsByKey);
  const detailRows = buildDetailRows(groups, source);
  const tenantProtectionOverlay = payload?.tenantProtectionOverlay || null;
  const hasBaselineSnapshot = payload?.status !== 'empty';
  const baselineDate = source?.baselineAsOfLabel || formatDate(source?.baselineAsOf);

  return (
    <article
      id="regulation-snapshot-card"
      className="research-detail-card research-detail-card--regulation-snapshot"
    >
      <div className="regulation-snapshot__header">
        <div>
          <h2 className="regulation-snapshot__title">
            {payload?.title || 'Landlord / Tenant Regulation Snapshot'}
          </h2>
        </div>
        {!loading && !error && hasBaselineSnapshot ? (
          <div className="regulation-snapshot__status-group">
            <div className={`regulation-snapshot__status regulation-snapshot__status--${friction.tone || 'default'}`}>
              Regulatory friction: {friction.label}
            </div>
            <div className="regulation-snapshot__status regulation-snapshot__status--date">
              Data as of {baselineDate}
            </div>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="research-detail-state">Loading regulation snapshot…</div>
      ) : null}

      {!loading && error ? (
        <div className="research-detail-state research-detail-state--error">{error}</div>
      ) : null}

      {!loading && !error && payload?.status === 'empty' ? (
        <div className="regulation-snapshot__empty">
          <p>{payload?.emptyMessage || 'No regulation snapshot is loaded for this market yet.'}</p>
        </div>
      ) : null}

      {!loading && !error && hasBaselineSnapshot ? (
        <>
          <section className="regulation-snapshot__timeline-block">
            <div className="regulation-snapshot__panel-head">
              <h3>Eviction Process Timeline</h3>
            </div>
            <div className="regulation-snapshot__timeline">
              {timelineSteps.map((step, index) => (
                <RegulationTimelineStep key={step.key} step={step} index={index} />
              ))}
            </div>
          </section>

          <div className="regulation-snapshot__disclosures">
            <details className="regulation-snapshot__details">
              <summary>View Source Table</summary>
            <div className="regulation-snapshot__details-table-wrap">
              <table className="regulation-snapshot__details-table">
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Value</th>
                    <th scope="col">Investor Signal</th>
                    <th scope="col">Source</th>
                    <th scope="col">Effective Date</th>
                    <th scope="col">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.field}</td>
                      <td>{row.value}</td>
                      <td>
                        <span
                          className={`regulation-snapshot__details-signal regulation-snapshot__details-signal--${row.investorSignal.tone}`}
                          title={row.investorSignal.description}
                          aria-label={`${row.investorSignal.label}: ${row.investorSignal.description}`}
                        >
                          <span aria-hidden="true" className="regulation-snapshot__details-signal-icon">
                            {row.investorSignal.symbol}
                          </span>
                          <span>{row.investorSignal.label}</span>
                        </span>
                      </td>
                      <td>{row.citation}</td>
                      <td>{row.effectiveDate}</td>
                      <td>{row.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </details>

            <RegulationTenantProtectionOverlay
              overlay={tenantProtectionOverlay}
              compact
            />
          </div>

        </>
      ) : null}
    </article>
  );
}
